/**
 * A module for generating an interactive site map widget
 *
 * If the module's init function is run, it will look for elements with the class sitebuilder-site-map-container on them
 * to use as containers.
 *
 * Settings are controlled with data- attributes as follows:
 *
 * <div class="sitebuilder-site-map-container"
 *  data-title="My sitemap title" [optional]
 *  data-root-page="/path/to/my/page"
 *  data-show-files="true" [optional - anything not "true" treated as false]
 *  data-show-redirects="true" [optional - anything not "true" treated as false]
 *  data-show-details="true" [optional - anything not "true" treated as false]
 * ></div>
 *
 * data-show-details refers to the information about subpage and file counts, or file sizes
 *
 * The container dispatches a 'sitemapUpdated' event whenever the content changes (including showing/hiding pages)
 * */

import { i, ul, li, span, button, a, div, h2 } from '../utils/html-builders';
import { MimeTypeIcons } from '../pagemaker/mimetype-icons';
import { consola } from 'consola';

const showChildrenText = "Show subpages and files under ";
const hideChildrenText = "Hide subpages and files under ";
const mimeTypes = new MimeTypeIcons();
// We might be calling this from the edit servlet, in which case the page query string should take precedence
const currentUrl = new URL(window.location.href);
const siteMapPage = currentUrl.searchParams.get('page') ?? window.location.pathname;
const loadingIconSelector = '.site-map-loading-icon';

function dataRequestURL(pageURL: string)  {
  return `/sitebuilder2/api/sitemap/pages.json?site_map_page=${encodeURIComponent(siteMapPage)}&page=${encodeURIComponent(pageURL)}`;
}

function accessibleIcon(iconClasses: string, accessibleText: string): DocumentFragment {
  const fragment = document.createDocumentFragment();
  fragment.appendChild(i({class: iconClasses, title: accessibleText, 'aria-hidden': 'true'}));
  fragment.appendChild(span({class: 'sr-only'}, accessibleText))
  return fragment;
}

function lockIcon(): DocumentFragment {
  return accessibleIcon("fas fa-fw fa-lock site-map-public", "Restricted permissions");
}

function siteRootIcon(): DocumentFragment {
  return accessibleIcon("fas fa-fw fa-sitemap site-map-site-root", "This is a site root page");
}

function loadingIcon(): HTMLElement {
  return div({class: 'site-map-loading-icon'},
    i({
      class: 'fas fa-cog fa-spin',
      'aria-hidden': 'true',}),
    span({
      class: 'sr-only'
    }, "Loading")
  );
}

function errorIcon(): HTMLElement {
  return div({class: 'site-map-error-icon text-danger'},
    i({
      class: 'fas fa-exclamation-circle',
      'aria-hidden': 'true',}),
    span({}, ' There was a problem fetching data for this page'));
}

function pluralise(count: number | undefined, itemName: string): string {
  if (count !== undefined) {
    const s = (count === 0 || count > 1)
      ? 's'
      : '';
    return `${itemName}${s}`;
  }
  return '';
}

async function fetchPageData(pageURL: string): Promise<PageData> {
  const response = await fetch(dataRequestURL(pageURL));
  return await response.json();
}

/** The JSON data structure we get from the server */
interface PageData {
  id: string,
  url: string,
  title: string,
  mimeType: string,
  hasChildren: boolean,
  hasSubpages: boolean,
  hasFiles: boolean,
  hasForwardingChildren: boolean,
  subpageCount?: number,
  fileCount?: number,
  totalFileSize?: number,
  isPublic: boolean,
  isHTML: boolean,
  isRedirect: boolean,
  targetPage?: string,
  isSiteRoot: boolean,
  children: PageData[],
}

class Page {
  private data: PageData;
  children: Page[];
  childIndex: number;
  setSize: number;
  parent: Page | null;
  hasDetail: boolean;
  /** The map that owns this Page */
  map: SiteMap
  treeLevel: number;
  element: HTMLElement;
  /** This flag gets set if the settings rule out showing any of the children returned */
  stopLookingForChildren: boolean;

  constructor(data: PageData, childIndex: number, siblingCount: number, parent: Page | null, map: SiteMap) {
    this.data = data;
    this.children = [];
    this.childIndex = childIndex;
    this.setSize = siblingCount;
    this.parent = parent;
    this.hasDetail = data.subpageCount !== undefined || data.fileCount !== undefined
    this.map = map;
    this.stopLookingForChildren = false;
    this.treeLevel = parent
      ? parent.treeLevel + 1
      : 1;

    this.map.pages[this.id] = this;
    this.element = this.asHTML();
    if (data.children?.length > 0) {
      this.addChildren(data.children);
    }

    this.dispatchUpdatedEvent();
  }

  private get hasShowableChildren(): boolean {
    return  (this.hasSubpages)
      || (this.map.settings.showFiles && this.data.hasFiles)
      || (this.map.settings.showRedirects && this.data.hasForwardingChildren);
  }

  private dispatchUpdatedEvent() {
    this.map.dispatchUpdatedEvent();
  }

  get loadingIcon() {
    return this.element.querySelector(loadingIconSelector);
  }

  addChildren(childrenData: PageData[] | null) {
    if (this.loadingIcon) {
      this.loadingIcon.remove();
    }

    const filteredChildrenData = childrenData?.filter((data) => {
      return data.isHTML || (data.isRedirect && this.map.settings.showRedirects) || (!data.isRedirect && !data.isHTML && this.map.settings.showFiles);
    });

    if (!filteredChildrenData || filteredChildrenData.length === 0 || this.childListElement) {
      // Skip it if there are no children or if we already have them rendered, and set the flag to stop further lookups
      this.stopLookingForChildren = true;
      return;
    }

    filteredChildrenData.forEach((child, index) => {
      this.children.push(new Page(child, index + 1, childrenData.length, this, this.map));
    });
    this.element.appendChild(ul({
      role: 'group',
      class: 'site-map-sublist list-unstyled'
    }, this.children.map(c => c.element)));
    this.dispatchUpdatedEvent();
  }

  asHTML(): HTMLElement {
    const classes = `site-map-page${this.hasShowableChildren ? ' has-children' : ''}`;
    const pageElement = li({
      role: 'treeitem',
      'aria-level': this.treeLevel.toString(10),
      'aria-setsize': this.setSize.toString(10),
      'aria-posinset': this.childIndex.toString(10),
      id: this.id,
      class: classes
    });

    // The icon
    if (this.isHTML) {
      if (this.isExpandable) {
        pageElement.appendChild(this.createChildButton());
      } else {
        pageElement.appendChild(accessibleIcon("site-map-mime-type-icon far fa-file", "Web page"))
      }
    } else { // Not HTML
      pageElement.appendChild(this.createMimeTypeIcon());
    }

    // The page title link
    const title = this.isRedirect ? `${this.title} - redirects to ${this.targetPage}` : this.title;
    pageElement.appendChild(a({
      href: (this.map.settings.baseUrl ?? '') + this.url,
      "data-page-url": this.url,
      class: 'site-map-page-title'
    }, title));

    // Extra icons
    if (!this.isPublic) { pageElement.appendChild(lockIcon()); }
    if (this.isSiteRoot) { pageElement.appendChild(siteRootIcon()); }

    // Detail - sub page/file count, file size
    if (this.hasDetail) { pageElement.appendChild(this.createDetailElement()); }

    return pageElement;
  }

  createChildButton(): HTMLElement {
    const childrenButton = button({
      type: 'button',
      class: 'btn btn-link btn-xs brand-text children-button'
    }, accessibleIcon("far fa-file-plus folder-icon", showChildrenText + this.title));
    childrenButton.addEventListener('click', () => {
      this.toggleChildren();
    });
    return childrenButton;
  }

  showChildren() {
    if (this.hasShowableChildren) {
      if (this.children.length === 0) {
        this.element.classList.add('open');
        this.element.appendChild(loadingIcon());
        this.loadChildren(); // loadChildren will sort out all the children elements and call this again
      } else {
        const icon = this.element.querySelector(".folder-icon");
        const text = this.element.querySelector<HTMLElement>(".sr-only");
        this.element.classList.add("open");
        icon.classList.remove("fa-file-plus");
        icon.classList.add("fa-file-minus");
        icon.setAttribute("title", hideChildrenText + this.title)
        text.innerText = hideChildrenText + this.title;
      }
      this.dispatchUpdatedEvent();
    }
  }

  hideChildren() {
    if (this.hasShowableChildren) {
      const icon = this.element.querySelector(".folder-icon");
      const text = this.element.querySelector<HTMLElement>(".sr-only");
      this.element.classList.remove("open");
      icon.classList.add("fa-file-plus");
      icon.classList.remove("fa-file-minus");
      icon.setAttribute("title", showChildrenText + this.title)
      text.innerText = showChildrenText + this.title;
      this.dispatchUpdatedEvent();
    }
  }

  toggleChildren() {
    if (this.element.classList.contains("open")) {
      this.hideChildren();
    } else {
      this.showChildren();
    }
  }

  createMimeTypeIcon(): DocumentFragment {
    if (this.isRedirect) {
      return accessibleIcon("site-map-mime-type-icon fas fa-arrow-alt-circle-right", "Forwarding page");
    } else {
      const iconData = mimeTypes.getDataForMimeType(this.mimeType)
      return accessibleIcon(`site-map-mime-type-icon fas fa${iconData.faExtension}`, iconData.description);
    }
  }

  createDetailElement(): HTMLElement {
    return div({class: 'site-map-details small'},
      `${this.subpageCount} ${pluralise(this.subpageCount, 'page')} ${this.fileCount} ${pluralise(this.fileCount, ' file')}${((this.totalFileSize ?? '0 B') !== '0 B' ? ` (${this.totalFileSize})` : '')}`);
  }

  loadChildren() {
    if (this.hasShowableChildren && !this.stopLookingForChildren && this.children.length === 0) {
      fetchPageData(this.url).then((data) => {
        this.addChildren(data.children);
        this.showChildren();
      }).catch((error) => {
        this.loadingIcon?.remove();
        this.element.appendChild(errorIcon());
        consola.error(`Error getting child page data for ${this.url}`, error)
      });
    }
  }

  get id() { return this.data.id; }
  get url() { return this.data.url; }
  get title() { return this.data.title; }
  get mimeType() { return this.data.mimeType; }
  get hasChildren() { return this.data.hasChildren; }
  get hasSubpages() { return this.data.hasSubpages; }
  get hasFiles() { return this.data.hasFiles; }
  get subpageCount() { return this.data.subpageCount; }
  get fileCount() { return this.data.fileCount; }
  get totalFileSize() { return this.data.totalFileSize; }
  get isPublic() { return this.data.isPublic; }
  get isHTML() { return this.data.isHTML; }
  get isRedirect() { return this.data.isRedirect; }
  get targetPage() { return this.data.targetPage; }
  get isSiteRoot() { return this.data.isSiteRoot; }
  get isExpandable() {return this.hasSubpages || (this.map.settings.showFiles && this.hasFiles)}
  get childListElement(): HTMLElement | null { return this.element.querySelector('ul.site-map-sublist'); }
}

interface SiteMapSettings {
  title?: string | undefined,
  rootPageURL: string,
  baseUrl?: string | undefined,
  showFiles: boolean | undefined,
  showDetails: boolean | undefined,
  showRedirects: boolean | undefined,
}

export class SiteMap {
  /** A map of page ids to page objects */
  pages: {[key: string]: Page};
  rootPage: Page;
  settings: SiteMapSettings;
  id: number;
  element: HTMLElement;

  constructor(target: HTMLElement) {
    this.id = nextSiteMapId;
    nextSiteMapId += 1;
    this.element = target;
    this.pages = {};
    this.settings = {
      title: target.dataset?.title,
      rootPageURL: target.dataset?.rootPage,
      baseUrl: target.dataset?.baseUrl,
      showFiles: target.dataset?.showFiles === 'true',
      showDetails: target.dataset?.showDetails === 'true',
      showRedirects: target.dataset?.showRedirects === 'true',
    }

    if (this.settings.title ?? '' !== '') {
      target.appendChild(h2({
        class: 'site-map-heading',
        id: `site-map-heading-${this.id}`,
      }, this.settings.title));
    }

    const mainList = ul({
      class: 'treeview list-unstyled',
      role: 'tree',
      'aria-live': 'polite',
    }, li({},
      loadingIcon()));

    target.appendChild(mainList);

    fetchPageData(this.settings.rootPageURL).then((data) => {
      this.rootPage = new Page(data, 1, 1, null, this);
      mainList.innerHTML = '';
      mainList.appendChild(this.rootPage.element);
      this.rootPage.showChildren();
    }).catch((error) => {
      this.element.querySelector(loadingIconSelector).remove();
      this.element.appendChild(errorIcon());
      consola.error("Error fetching sitemap data", error);
    });
  }

  dispatchUpdatedEvent() {
    this.element.dispatchEvent(new Event('sitemapUpdated'));
  }
}

const allSiteMaps = [];
let nextSiteMapId = 0;

export function init() {
  const targets = document.querySelectorAll('.sitebuilder-site-map-container');
  targets.forEach((target: HTMLElement) => {
    allSiteMaps.push(new SiteMap(target));
  });
}

// We import this file only where needed to avoid bloating the SiteBuilder front end, hence the init bit living here
window.addEventListener('DOMContentLoaded', () => {
  init();
})
